import { useState, useEffect, useMemo } from "react";
import { useCategory } from "../../../queries/useCategories";
import { useCategoryAttributesWithValues } from "../../../queries/useCategoryAttributesWithValues";
import type { Category } from "@/services/api/categories";
import type { CategoryAttributeResponse } from "@/services/api";
import type { CategoryAttributeRequest } from "@/services/api/categories";

export interface SelectedAttribute {
  attributeId: number;
  isMandatory: boolean;
  order: number;
}

export interface CategoryFormData {
  name: string;
  displayName: string;
  icon: string;
  selectedAttributes: SelectedAttribute[];
}

interface UseCategoryFormDataOptions {
  category?: Category;
  open: boolean;
}

function mapCategoryAttributesToSelected(
  categoryAttributes: CategoryAttributeRequest[] | undefined,
  availableAttributes: CategoryAttributeResponse[]
): SelectedAttribute[] {
  if (!categoryAttributes || availableAttributes.length === 0) return [];

  return categoryAttributes
    .map((catAttr) => {
      const attrId =
        typeof catAttr.attributeId === "number"
          ? catAttr.attributeId
          : parseInt(String(catAttr.attributeId), 10);
      const availableAttr = availableAttributes.find((a) => a.id === attrId);
      return availableAttr
        ? {
            attributeId: availableAttr.id,
            isMandatory: catAttr.isMandatory ?? false,
            order: catAttr.order ?? 2,
          }
        : null;
    })
    .filter((attr): attr is SelectedAttribute => attr !== null);
}

export function useCategoryFormData({
  category,
  open,
}: UseCategoryFormDataOptions) {
  const { data: availableAttributes = [] } = useCategoryAttributesWithValues({
    enabled: open,
  });
  const { data: categoryDetails, isLoading: isLoadingCategory } = useCategory(
    category?.id || "",
    { enabled: open && !!category?.id }
  );

  const mappedAttributes = useMemo(() => {
    if (!categoryDetails?.attributes || availableAttributes.length === 0)
      return [];
    return mapCategoryAttributesToSelected(
      categoryDetails.attributes,
      availableAttributes
    );
  }, [categoryDetails?.attributes, availableAttributes]);

  const [formData, setFormData] = useState<CategoryFormData>({
    name: "",
    displayName: "",
    icon: "",
    selectedAttributes: [],
  });

  useEffect(() => {
    if (!open) {
      setFormData((prev) => {
        if (
          prev.name === "" &&
          prev.displayName === "" &&
          prev.icon === "" &&
          prev.selectedAttributes.length === 0
        ) {
          return prev;
        }
        return { name: "", displayName: "", icon: "", selectedAttributes: [] };
      });
      return;
    }
    if (category && categoryDetails) {
      setFormData((prev) => {
        const newName = categoryDetails.name ?? "";
        const newDisplayName = categoryDetails.displayName ?? "";
        const newIcon = categoryDetails.icon ?? "";
        const nameChanged = prev.name !== newName;
        const displayNameChanged = prev.displayName !== newDisplayName;
        const iconChanged = prev.icon !== newIcon;
        const attributesChanged =
          prev.selectedAttributes.length !== mappedAttributes.length ||
          prev.selectedAttributes.some(
            (attr, index) =>
              attr.attributeId !== mappedAttributes[index]?.attributeId ||
              attr.isMandatory !== mappedAttributes[index]?.isMandatory ||
              attr.order !== mappedAttributes[index]?.order
          );

        if (!nameChanged && !displayNameChanged && !iconChanged && !attributesChanged) {
          return prev;
        }

        return {
          name: newName,
          displayName: newDisplayName,
          icon: newIcon,
          selectedAttributes: mappedAttributes,
        };
      });
    } else if (!category) {
      setFormData((prev) => {
        if (
          prev.name === "" &&
          prev.displayName === "" &&
          prev.icon === "" &&
          prev.selectedAttributes.length === 0
        ) {
          return prev;
        }
        return { name: "", displayName: "", icon: "", selectedAttributes: [] };
      });
    }
  }, [open, category, categoryDetails, mappedAttributes]);

  const updateFormData = (
    updates:
      | Partial<CategoryFormData>
      | ((prev: CategoryFormData) => Partial<CategoryFormData>)
  ) => {
    setFormData((prev) => {
      const newUpdates =
        typeof updates === "function" ? updates(prev) : updates;
      return { ...prev, ...newUpdates };
    });
  };

  return {
    formData,
    setFormData: updateFormData,
    availableAttributes,
    isLoading: isLoadingCategory && !!category,
  };
}
